import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bcmApi, type BcmNode } from '../api/bcmApi';
import { useBcmNavigation } from './useBcmNavigation';

export interface DisplayRule {
  id: string;
  label?: string;
  condition: {
    field: string;
    op: 'eq' | 'lt' | 'gt' | 'ne';
    value: unknown;
  };
  effect: {
    color?: string;
    opacity?: number;
    borderStyle?: string;
  };
}

function getFieldValue(node: BcmNode, field: string): unknown {
  if (field.startsWith('_computed.')) {
    const key = field.slice('_computed.'.length) as keyof NonNullable<BcmNode['entry']>['_computed'];
    return node.entry?._computed?.[key];
  }
  if (field.startsWith('attributes.')) {
    const key = field.slice('attributes.'.length);
    return node.entry?.attributes?.[key];
  }
  if (field === 'status') {
    return node.entry?.status;
  }
  return undefined;
}

function evaluateCondition(condition: DisplayRule['condition'], node: BcmNode): boolean {
  const value = getFieldValue(node, condition.field);
  switch (condition.op) {
    case 'eq':
      return value === condition.value;
    case 'ne':
      return value !== condition.value;
    case 'lt':
      return typeof value === 'number' && typeof condition.value === 'number' && value < condition.value;
    case 'gt':
      return typeof value === 'number' && typeof condition.value === 'number' && value > condition.value;
    default:
      return false;
  }
}

export function applyDisplayRules(node: BcmNode, rules: DisplayRule[]): CSSProperties {
  for (const rule of rules) {
    if (evaluateCondition(rule.condition, node)) {
      return rule.effect as CSSProperties;
    }
  }
  return {};
}

export interface UseBcmBoardResult {
  allNodes: BcmNode[];
  parentNodes: BcmNode[];
  childNodes: BcmNode[];
  loading: boolean;
  error: string | null;
  ghostCount: number;
  navigation: ReturnType<typeof useBcmNavigation>;
  addNode: (bcEntryId: string, parentBcEntryId: string | null, x: number, y: number) => Promise<void>;
  updatePosition: (nodeId: string, x: number, y: number, w: number, h: number) => Promise<void>;
  removeNode: (nodeId: string) => Promise<void>;
  sync: () => Promise<void>;
  displayRules: DisplayRule[];
}

export function useBcmBoard(
  projectId: string,
  mapEntryId: string,
  mapAttributes: Record<string, unknown>,
): UseBcmBoardResult {
  const [allNodes, setAllNodes] = useState<BcmNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ghostCount, setGhostCount] = useState(0);
  const navigation = useBcmNavigation();
  const initializedRef = useRef(false);
  const { initRoots, reset } = navigation;

  const displayRulesKey = JSON.stringify(mapAttributes?.display_rules ?? []);
  const rootBcIdsKey = JSON.stringify(mapAttributes?.root_bc_ids ?? null);

  const displayRules = useMemo(() => {
    try {
      return JSON.parse(displayRulesKey) as DisplayRule[];
    } catch {
      return [];
    }
  }, [displayRulesKey]);
  const rootBcIds = useMemo(() => {
    try {
      return JSON.parse(rootBcIdsKey) as string[] | null;
    } catch {
      return null;
    }
  }, [rootBcIdsKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nodes = await bcmApi.listNodes(projectId, mapEntryId);
      setAllNodes(nodes);
      if (!initializedRef.current) {
        initRoots(rootBcIds, nodes);
        initializedRef.current = true;
      }
    } catch (e: any) {
      setError(e.message ?? 'BCM load failed');
    } finally {
      setLoading(false);
    }
  }, [initRoots, projectId, mapEntryId, rootBcIds]);

  useEffect(() => {
    initializedRef.current = false;
    reset();
    void load();
  }, [load, projectId, mapEntryId, reset]);

  const currentParentIds = navigation.navState.currentParentIds;
  const isAtRootLevel = navigation.navState.breadcrumb.length === 0;

  const parentNodes = useMemo(() => [], []);

  const childNodes = useMemo(
    () => {
      if (isAtRootLevel) {
        return allNodes.filter((node) => currentParentIds.includes(node.bc_entry_id));
      }
      return allNodes.filter(
        (node) => !!node.entry?.parent_id && currentParentIds.includes(node.entry.parent_id),
      );
    },
    [allNodes, currentParentIds, isAtRootLevel],
  );

  const addNode = useCallback(
    async (bcEntryId: string, _parentBcEntryId: string | null, x: number, y: number) => {
      const created = await bcmApi.addNode(projectId, mapEntryId, { bc_entry_id: bcEntryId, x, y });
      setAllNodes((prev) => [...prev, created]);
    },
    [projectId, mapEntryId],
  );

  const updatePosition = useCallback(
    async (nodeId: string, x: number, y: number, w: number, h: number) => {
      const updated = await bcmApi.updateNodePosition(projectId, mapEntryId, nodeId, { x, y, w, h });
      setAllNodes((prev) => prev.map((node) => (node.id === nodeId ? { ...node, ...updated } : node)));
    },
    [projectId, mapEntryId],
  );

  const removeNode = useCallback(
    async (nodeId: string) => {
      await bcmApi.deleteNode(projectId, mapEntryId, nodeId);
      setAllNodes((prev) => prev.filter((node) => node.id !== nodeId));
    },
    [projectId, mapEntryId],
  );

  const sync = useCallback(async () => {
    const result = await bcmApi.syncMap(projectId, mapEntryId);
    setGhostCount(result.removed_ghost_count);
    await load();
  }, [load, projectId, mapEntryId]);

  return {
    allNodes,
    parentNodes,
    childNodes,
    loading,
    error,
    ghostCount,
    navigation,
    addNode,
    updatePosition,
    removeNode,
    sync,
    displayRules,
  };
}
